import { ArrowLeft, Calendar, Clock, MessageSquare, Check, Eye, Edit, Trash2, Plus, MapPin, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AppointmentDetailDialog } from "@/components/AppointmentDetailDialog";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import BottomNavigation from "@/components/BottomNavigation";
import { toast } from "sonner";

const AppointmentCalendar = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("book");
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState("");
  const [comments, setComments] = useState("");
  const [isBooked, setIsBooked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);

  // Edit form states
  const [editDate, setEditDate] = useState<Date | undefined>();
  const [editTime, setEditTime] = useState("");
  const [editComments, setEditComments] = useState("");
  // Mock appointments data
  const mockAppointments = [
    {
      id: "1",
      serviceTitle: "Mindful Life Coaching Session",
      providerName: "Sarah Moonlight",
      providerAvatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200",
      date: new Date(2025, 7, 15),
      time: "10:00",
      duration: "90 min",
      price: 85,
      status: 'confirmed' as const,
      location: "Online (Zoom)",
      comments: ""
    },
    {
      id: "2",
      serviceTitle: "Business Strategy Consultation", 
      providerName: "Lisa van der Berg",
      providerAvatar: "https://images.pexels.com/photos/3182812/pexels-photo-3182812.jpeg?auto=compress&cs=tinysrgb&w=200",
      date: new Date(2025, 7, 22),
      time: "14:30",
      duration: "60 min", 
      price: 120,
      status: 'pending' as const,
      location: "Amsterdam Centrum",
      comments: ""
    }
  ];
  const [appointments, setAppointments] = useState<Array<{
    id: string;
    serviceId?: string;
    serviceTitle?: string;
    providerName?: string;
    providerAvatar?: string;
    date: Date;
    time: string;
    comments: string;
    duration?: string;
    price?: number;
    status?: 'confirmed' | 'pending' | 'completed' | 'cancelled';
    location?: string;
  }>>(mockAppointments);

  // Mock service data - would come from props/context in real app
  const service = {
    id: "1",
    title: "Mindful Life Coaching Session",
    provider: {
      name: "Sarah Moonlight",
      avatar: "https://images.pexels.com/photos/3763188/pexels-photo-3763188.jpeg?auto=compress&cs=tinysrgb&w=200"
    },
    duration: "90 min",
    price: 85
  };

  // Voorbeeld van beschikbaarheid per weekdag
  const availability: Record<string, string[]> = {
    maandag: ["10:00", "11:30", "14:30"],
    dinsdag: ["09:00", "13:00"],
    woensdag: ["10:00", "13:00", "16:00"],
    donderdag: ["11:30", "14:30", "16:00"],
    vrijdag: ["10:00", "11:30"],
    // zaterdag, zondag: geen beschikbaarheid
  };

  // Mapping van weekdag nummer naar Nederlands
  const weekdaysNl = [
    'zondag', 'maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag', 'zaterdag'
  ];

  // Genereer time slots op basis van gekozen dag
  const getAvailableTimesForDate = (date?: Date) => {
    if (!date) return [];
    const weekdayName = weekdaysNl[date.getDay()];
    return availability[weekdayName] || [];
  }

  const handleConfirmAppointment = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error("Selecteer zowel een datum als tijd");
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      const newAppointment = {
        id: Date.now().toString(),
        serviceId: service.id,
        serviceTitle: service.title,
        providerName: service.provider.name,
        providerAvatar: service.provider.avatar,
        date: selectedDate!,
        time: selectedTime,
        comments: comments,
        duration: service.duration,
        price: service.price,
        status: 'confirmed' as const,
        location: "Online (Zoom)"
      };
      setAppointments((prev) => [...prev, newAppointment]);
      toast.success("Afspraak succesvol ingepland");
      setIsLoading(false);
      setIsBooked(true);
    }, 1500);
  };

  const handleGoBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleBookAnother = () => {
    setIsBooked(false);
    setSelectedDate(undefined);
    setSelectedTime("");
    setComments("");
    setActiveTab("book");
  };

  const handleGoToAppointments = () => {
    setActiveTab("manage");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800 border-green-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Bevestigd';
      case 'pending': return 'In afwachting';
      case 'completed': return 'Voltooid';
      case 'cancelled': return 'Geannuleerd';
      default: return status;
    }
  };

  const handleViewAppointment = (appointment: any) => {
    console.log("👁️ Opening appointment details for:", appointment.serviceTitle);
    setSelectedAppointment(appointment);
    setViewModalOpen(true);
  };

  const handleEditAppointment = (appointment: any) => {
    console.log("✏️ Opening edit modal for:", appointment.serviceTitle);
    setSelectedAppointment(appointment);
    setEditDate(appointment.date);
    setEditTime(appointment.time);
    setEditComments(appointment.comments || "");
    setEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editDate || !editTime || !selectedAppointment) {
      toast.error("Vul alle verplichte velden in");
      return;
    }

    // Update the appointments array
    setAppointments(prev => prev.map(apt => 
      apt.id === selectedAppointment.id 
        ? { ...apt, date: editDate, time: editTime, comments: editComments }
        : apt
    ));

    toast.success(`Afspraak "${selectedAppointment.serviceTitle}" is bijgewerkt!`);
    setEditModalOpen(false);
    setSelectedAppointment(null);
    setEditDate(undefined);
    setEditTime("");
    setEditComments("");
  };

  const handleCancelAppointment = (appointment: any) => {
    console.log("🗑️ Attempting to cancel appointment:", appointment.serviceTitle);
    if (window.confirm(`Weet je zeker dat je de afspraak "${appointment.serviceTitle}" wilt annuleren?`)) {
      // Actually remove the appointment from the state
      setAppointments(prev => {
        const newAppointments = prev.filter(apt => apt.id !== appointment.id);
        console.log("✅ Appointment removed, new count:", newAppointments.length);
        return newAppointments;
      });

      toast.success(`Afspraak geannuleerd!`, {
        description: `"${appointment.serviceTitle}" is verwijderd uit je agenda.`,
        duration: 4000,
        style: {
          background: '#dc2626',
          color: '#ffffff',
          borderColor: '#b91c1c'
        },
        action: {
          label: "Nieuwe afspraak",
          onClick: () => setActiveTab("book")
        }
      });
    }
  };
  // Success screen
  if (isBooked) {
    return (
      <>
        <div className="min-h-screen flex items-center justify-center p-6 bg-elegant pb-24">
          <Card className="w-full max-w-md card-premium">
            <CardContent className="p-8 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-2xl font-serif font-bold mb-2 text-navy-800">
                  Bedankt!
                </h2>
                <p className="text-navy-600">
                  Je afspraak is succesvol ingepland.
                </p>
              </div>

              <div className="rounded-xl p-4 mb-6 text-left bg-cream-100 border border-cream-200">
                <h3 className="font-semibold mb-2 text-navy-800">Afspraak Details:</h3>
                <div className="space-y-1 text-sm text-navy-600">
                  <p><strong>Service:</strong> {service.title}</p>
                  <p><strong>Provider:</strong> {service.provider.name}</p>
                  <p><strong>Datum:</strong> {selectedDate && format(selectedDate, 'dd MMMM yyyy', { locale: nl })}</p>
                  <p><strong>Tijd:</strong> {selectedTime}</p>
                  <p><strong>Duur:</strong> {service.duration}</p>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={handleBookAnother}
                  className="w-full btn-elegant-primary"
                >
                  Boek Nog Een Afspraak
                </Button>
                <Button 
                  onClick={handleGoToAppointments}
                  variant="outline"
                  className="w-full btn-elegant-secondary"
                >
                  Mijn Afspraken Bekijken
                </Button>
                <Button 
                  onClick={handleGoBack}
                  variant="outline"
                  className="w-full btn-elegant-secondary"
                >
                  Terug naar Service
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
        <BottomNavigation />
      </>
    );
  }

  const filteredTimeSlots = getAvailableTimesForDate(selectedDate);

  return (
    <>
      <div className="min-h-screen bg-elegant pb-24">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-cream-300 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center gap-4 mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleGoBack}
                className="p-2 hover:bg-cream-100 text-navy-600"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-serif font-semibold text-navy-800">
                  Afspraak Inplannen
                </h1>
                <p className="text-sm text-navy-600">
                  Kies je gewenste datum en tijd
                </p>
              </div>
            </div>

            {/* WOM Plaza Header */}
            <div className="rounded-lg p-4 mb-4 bg-cream-200 border border-cream-300">
              <div className="text-center">
                <div className="mb-2">
                  <h1 className="text-2xl font-light tracking-wider text-navy-700">
                    <span className="font-bold text-navy-800">W</span>
                    <span className="mx-1 text-gold-400">·</span>
                    <span className="font-bold text-navy-800">O</span>
                    <span className="mx-1 text-gold-400">·</span>
                    <span className="font-bold text-navy-800">M</span>
                    <span className="ml-3 font-bold text-navy-800">Afspraak</span>
                  </h1>
                </div>
                <p className="text-sm text-navy-600">
                  Maak een afspraak met een woman on a mission
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">
          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-cream-200">
              <TabsTrigger value="book" className="flex items-center gap-2 data-[state=active]:bg-white text-navy-700">
                <Plus className="h-4 w-4" />
                Afspraak Boeken
              </TabsTrigger>
              <TabsTrigger value="manage" className="flex items-center gap-2 data-[state=active]:bg-white text-navy-700">
                <Calendar className="h-4 w-4" />
                Mijn Afspraken ({appointments.length})
              </TabsTrigger>
            </TabsList>

            {/* Book Appointment Tab */}
            <TabsContent value="book">
              {/* Title */}
              <div className="mb-6">
                <h2 className="text-3xl font-serif font-bold mb-2 text-navy-800 text-center">
                  Plan je afspraak
                </h2>
                <p className="text-navy-600 text-center">
                  Kies je gewenste datum en tijd voor de sessie.
                </p>
              </div>

              {/* Service Description Card */}
              <Card className="mb-6 card-premium">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={service.provider.avatar} alt={service.provider.name} />
                      <AvatarFallback className="bg-cream-200 text-navy-700">
                        {service.provider.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-navy-800">{service.title}</h3>
                      <p className="text-sm text-navy-600">met {service.provider.name}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-navy-600">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{service.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span>€{service.price}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Booking Form */}
              <Card className="card-premium">
                <CardHeader>
                  <CardTitle className="text-xl text-navy-800 text-center">Selecteer Datum & Tijd</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Date Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="date" className="font-medium flex items-center gap-2 text-navy-700 justify-center">
                      <Calendar className="h-4 w-4" />
                      Kies datum
                    </Label>
                    <div className="bg-cream-100 rounded-xl border border-cream-200 overflow-hidden max-w-md mx-auto">
                      <div className="p-4 flex justify-center">
                        <div className="w-full max-w-sm">
                          <CalendarPicker
                            mode="single"
                            selected={selectedDate}
                            onSelect={date => {
                              setSelectedDate(date ?? undefined);
                              setSelectedTime(""); // reset tijd
                            }}
                            disabled={(date) => {
                              // Disable past dates and weekends
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                              return date < today || isWeekend;
                            }}
                            fromDate={new Date()}
                            toDate={new Date(new Date().setMonth(new Date().getMonth() + 6))}
                            locale={nl}
                            className="w-full bg-transparent border-0"
                            classNames={{
                              months: "flex flex-col space-y-4 w-full",
                              month: "space-y-4 w-full",
                              caption: "flex justify-center pt-1 relative items-center text-navy-800 pb-4",
                              caption_label: "text-lg font-semibold px-12",
                              nav: "space-x-1 flex items-center",
                              nav_button: "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-cream-300 bg-white shadow-sm hover:bg-cream-100 hover:text-navy-800 h-8 w-8 p-0",
                              nav_button_previous: "absolute left-1",
                              nav_button_next: "absolute right-1",
                              table: "w-full border-collapse space-y-1 mx-auto",
                              head_row: "flex w-full mb-2 justify-center",
                              head_cell: "text-navy-600 rounded-md font-medium text-xs uppercase tracking-wide py-2 text-center w-full min-h-[2rem] flex items-center justify-center",
                              row: "flex w-full mt-1 justify-center",
                              cell: "relative text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-navy-100 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-navy-100/50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md w-full min-h-[2.5rem] flex items-center justify-center p-0",
                              day: "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-cream-200 hover:text-navy-800 h-10 w-10 font-normal aria-selected:opacity-100 text-center mx-auto",
                              day_range_end: "day-range-end",
                              day_selected: "bg-navy-700 text-white hover:bg-navy-800 hover:text-white focus:bg-navy-700 focus:text-white",
                              day_today: "bg-gold-200 text-navy-800 font-semibold hover:bg-gold-300",
                              day_outside: "day-outside text-navy-400 opacity-50 aria-selected:bg-navy-100/50 aria-selected:text-navy-500 aria-selected:opacity-30",
                              day_disabled: "text-navy-400 opacity-50 cursor-not-allowed",
                              day_range_middle: "aria-selected:bg-navy-100 aria-selected:text-navy-800",
                              day_hidden: "invisible",
                            }}
                            components={{
                              IconLeft: () => <ArrowLeft className="h-4 w-4" />,
                              IconRight: () => <ArrowLeft className="h-4 w-4 rotate-180" />,
                            }}
                          />
                        </div>
                      </div>
                      {selectedDate && (
                        <div className="bg-white border-t border-cream-200 p-4">
                          <div className="text-center text-base font-medium text-navy-700">
                            <span className="text-sm text-navy-600">Geselecteerde datum:</span>
                            <br />
                            <span className="font-semibold">{format(selectedDate, 'eeee d MMMM yyyy', { locale: nl })}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Time Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="time" className="font-medium flex items-center gap-2 text-navy-700 justify-center">
                      <Clock className="h-4 w-4" />
                      Kies tijd
                    </Label>
                    {selectedDate && filteredTimeSlots.length === 0 && (
                      <div className="py-3 px-4 bg-cream-100 border border-cream-200 rounded-lg text-navy-600 text-sm text-center">
                        Geen tijden beschikbaar op deze dag
                      </div>
                    )}
                    <Select value={selectedTime} onValueChange={setSelectedTime} disabled={!selectedDate || filteredTimeSlots.length === 0}>
                      <SelectTrigger className="w-full max-w-md mx-auto border-cream-300 bg-white focus:border-gold-400 focus:ring-gold-400">
                        <SelectValue placeholder={selectedDate ? (filteredTimeSlots.length === 0 ? "Geen tijd beschikbaar" : "Selecteer een tijd") : "Eerst datum kiezen"} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-cream-300 z-50">
                        {filteredTimeSlots.map((tijd) => (
                          <SelectItem key={tijd} value={tijd} className="hover:bg-cream-50">
                            {tijd}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Comments */}
                  <div className="space-y-2">
                    <Label htmlFor="comments" className="font-medium flex items-center gap-2 text-navy-700 justify-center">
                      <MessageSquare className="h-4 w-4" />
                      Opmerkingen (optioneel)
                    </Label>
                    <Textarea 
                      id="comments"
                      placeholder="Eventuele speciale wensen of informatie die je wilt delen..."
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      className="min-h-[100px] max-w-md mx-auto border-cream-300 focus:border-gold-400 focus:ring-gold-400"
                    />
                  </div>

                  {/* Confirm Button */}
                  <div className="flex justify-center">
                    <Button 
                      onClick={handleConfirmAppointment}
                      disabled={!selectedDate || !selectedTime || isLoading}
                      className="w-full max-w-md py-4 text-lg font-semibold rounded-xl btn-elegant-primary"
                      size="lg"
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Afspraak inplannen...
                        </>
                      ) : (
                        'Bevestig afspraak'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Manage Appointments Tab */}
            <TabsContent value="manage">
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-3xl font-serif font-bold mb-2 text-navy-800">
                    Mijn Afspraken
                  </h2>
                  <p className="text-navy-600">
                    Bekijk en beheer je gemaakte afspraken
                  </p>
                </div>

                {appointments.length > 0 ? (
                  <div className="space-y-4">
                    {appointments.map((appointment) => (
                      <Card key={appointment.id} className="bg-white rounded-lg shadow-sm border border-cream-300 hover:shadow-md transition-all duration-300">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-12 w-12">
                                <AvatarImage src={appointment.providerAvatar} alt={appointment.providerName} />
                                <AvatarFallback className="bg-cream-200 text-navy-700">
                                  {appointment.providerName?.split(' ').map(n => n[0]).join('') || 'WP'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <h3 className="font-semibold text-navy-800">{appointment.serviceTitle}</h3>
                                <p className="text-sm text-navy-600">met {appointment.providerName}</p>
                              </div>
                            </div>
                            <Badge className={getStatusColor(appointment.status || 'confirmed')}>
                              {getStatusText(appointment.status || 'confirmed')}
                            </Badge>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-navy-600 mb-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>{format(appointment.date, 'eeee d MMMM yyyy', { locale: nl })}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>{appointment.time} ({appointment.duration || '60 min'})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">€{appointment.price || 0}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              <span>{appointment.location || 'Online'}</span>
                            </div>
                          </div>

                          <div className="flex gap-2 justify-end">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-2 hover:bg-cream-100 text-navy-600" 
                              title="👁️ Bekijk afspraak details" 
                              onClick={() => handleViewAppointment(appointment)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-2 hover:bg-amber-100 text-amber-600" 
                              title="Bewerk afspraak" 
                              onClick={() => handleEditAppointment(appointment)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-2 hover:bg-rose-100 text-rose-600" 
                              title="Annuleer afspraak" 
                              onClick={() => handleCancelAppointment(appointment)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="h-16 w-16 text-navy-400 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-navy-800 mb-2">Nog geen afspraken</h3>
                    <p className="text-navy-600 mb-6">
                      Boek je eerste afspraak met een woman on a mission
                    </p>
                    <Button 
                      onClick={() => setActiveTab("book")}
                      className="btn-elegant-primary"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Eerste afspraak boeken
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* View Appointment Modal */}
      <AppointmentDetailDialog 
        open={viewModalOpen} 
        onOpenChange={setViewModalOpen} 
        appointment={selectedAppointment} 
      />
      {/* Edit Appointment Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Afspraak Bewerken</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-date" className="font-medium flex items-center gap-2 text-navy-700">
                <Calendar className="h-4 w-4" />
                Datum
              </Label>
              <CalendarPicker
                mode="single"
                selected={editDate}
                onSelect={date => setEditDate(date ?? undefined)}
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  return date < today || isWeekend;
                }}
                fromDate={new Date()}
                toDate={new Date(new Date().setMonth(new Date().getMonth() + 6))}
                locale={nl}
                className="w-full bg-transparent border-0"
                classNames={{
                  months: "flex flex-col space-y-4 w-full",
                  month: "space-y-4 w-full",
                  caption: "flex justify-center pt-1 relative items-center text-navy-800 pb-4",
                  caption_label: "text-lg font-semibold px-12",
                  nav: "space-x-1 flex items-center",
                  nav_button: "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-cream-300 bg-white shadow-sm hover:bg-cream-100 hover:text-navy-800 h-8 w-8 p-0",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1 mx-auto",
                  head_row: "flex w-full mb-2 justify-center",
                  head_cell: "text-navy-600 rounded-md font-medium text-xs uppercase tracking-wide py-2 text-center w-full min-h-[2rem] flex items-center justify-center",
                  row: "flex w-full mt-1 justify-center",
                  cell: "relative text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-navy-100 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-navy-100/50 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md w-full min-h-[2.5rem] flex items-center justify-center p-0",
                  day: "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 hover:bg-cream-200 hover:text-navy-800 h-10 w-10 font-normal aria-selected:opacity-100 text-center mx-auto",
                  day_range_end: "day-range-end",
                  day_selected: "bg-navy-700 text-white hover:bg-navy-800 hover:text-white focus:bg-navy-700 focus:text-white",
                  day_today: "bg-gold-200 text-navy-800 font-semibold hover:bg-gold-300",
                  day_outside: "day-outside text-navy-400 opacity-50 aria-selected:bg-navy-100/50 aria-selected:text-navy-500 aria-selected:opacity-30",
                  day_disabled: "text-navy-400 opacity-50 cursor-not-allowed",
                  day_range_middle: "aria-selected:bg-navy-100 aria-selected:text-navy-800",
                  day_hidden: "invisible",
                }}
                components={{
                  IconLeft: () => <ArrowLeft className="h-4 w-4" />,
                  IconRight: () => <ArrowLeft className="h-4 w-4 rotate-180" />,
                }}
              />
            </div>

            <div>
              <Label htmlFor="edit-time" className="font-medium flex items-center gap-2 text-navy-700">
                <Clock className="h-4 w-4" />
                Tijd
              </Label>
              <Input
                id="edit-time"
                type="time"
                value={editTime}
                onChange={(e) => setEditTime(e.target.value)}
                className="max-w-md mx-auto"
              />
            </div>

            <div>
              <Label htmlFor="edit-comments" className="font-medium flex items-center gap-2 text-navy-700">
                <MessageSquare className="h-4 w-4" />
                Opmerkingen
              </Label>
              <Textarea
                id="edit-comments"
                value={editComments}
                onChange={(e) => setEditComments(e.target.value)}
                className="min-h-[100px] max-w-md mx-auto"
              />
            </div>
          </div>
          <DialogFooter className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>Annuleren</Button>
            <Button onClick={handleSaveEdit}>Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BottomNavigation />
    </>
  );
};

export default AppointmentCalendar;